import { useCallback, useState } from 'react';
import { WizardForm } from '@react-admin/ra-form-layout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  AutocompleteInput,
  ReferenceInput,
  required,
  type Identifier,
  useCanAccess,
  useGetMany,
  useListContext,
  useNotify,
  useRefresh,
  DataTable,
  ReferenceField,
  TextField,
  useList,
} from 'react-admin';
import { Alert, Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { type FieldValues } from 'react-hook-form';
import MappingRuleResource from '../../../Entities/MappingRules';
import { useMappingRuleCategoryBulkRemap } from '../../../DataProvider';
import { createFilterQuery, getJsonApiErrorDetail } from '../../../utils';
import OfferCountField from '../OfferCountField';

interface BulkUpdateCategoryDialogProps {
  open: boolean;
  onClose: () => void;
}

interface CategoryFormValues {
  categoryId: string;
}

export default function BulkUpdateCategoryDialog({
  open,
  onClose: onCloseParent,
}: BulkUpdateCategoryDialogProps) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [bulkRemapCategory, bulkRemapState] = useMappingRuleCategoryBulkRemap();

  const { selectedIds, onUnselectItems } = useListContext();

  const merchantListAccess = useCanAccess({ resource: 'merchants', action: 'list' });
  const merchantStatusListAccess = useCanAccess({ resource: 'merchant_statuses', action: 'list' });
  const regionListAccess = useCanAccess({ resource: 'regions', action: 'list' });
  const categoryListAccess = useCanAccess({ resource: 'categories', action: 'list' });

  const [relatedOfferIds, setRelatedOfferIds] = useState<Set<Identifier>>(new Set());

  const prefetch: string[] = [];
  if (merchantListAccess.canAccess ?? false) prefetch.push('merchant');
  if (merchantStatusListAccess.canAccess ?? false) prefetch.push('merchant.merchant_status');
  if (regionListAccess.canAccess ?? false) prefetch.push('merchant.region');
  if (categoryListAccess.canAccess ?? false) prefetch.push('category');

  const mappingRules = useGetMany<MappingRuleResource>(
    'mapping_rules',
    { ids: selectedIds, meta: { prefetch } },
    { enabled: open && selectedIds.length > 0 },
  );

  // Keep sorting local to the dialog table instead of the parent list context.
  const localList = useList({
    data: mappingRules.data,
    sort: { field: 'id', order: 'ASC' },
  });

  const isSubmitting = bulkRemapState.isPending;

  const resetDialogState = useCallback(() => {
    setRelatedOfferIds(new Set());
    localList.setSort({ field: 'id', order: 'ASC' });
  }, [localList]);

  const handleDialogClose = useCallback(() => {
    resetDialogState();
    onCloseParent();
  }, [onCloseParent, resetDialogState]);

  const handleSubmit = async (values: FieldValues): Promise<void> => {
    const formValues = values as CategoryFormValues;
    const selectedCategoryId = formValues.categoryId.trim();

    if (!selectedCategoryId) {
      notify('Choose a category first', { type: 'warning' });
      return;
    }

    try {
      await bulkRemapCategory({
        mappingRuleIds: selectedIds,
        toCategoryId: Number(selectedCategoryId),
        mappingRuleIdsToUpdateOffers: [...relatedOfferIds].map((id) => Number(id)),
      });
    } catch (error: unknown) {
      notify(getJsonApiErrorDetail(error, 'Bulk remap failed'), { type: 'error' });
      return;
    }

    onUnselectItems();
    notify(
      relatedOfferIds.size > 0
        ? 'Mapping rules and related offers updated'
        : 'Mapping rules updated',
      { type: 'success' },
    );
    refresh();
    handleDialogClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_event, reason) => {
        if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        handleDialogClose();
      }}
    >
      <DialogTitle>Map To Category</DialogTitle>
      <DialogContent>
        {isSubmitting ? (
          <Alert icon={<WarningAmberIcon fontSize="inherit" />} severity="warning" sx={{ mb: 2 }}>
            Saving changes. Please do not reload or close this page. Updating a large number of
            offers can take a while.
          </Alert>
        ) : null}
        <Box
          component="fieldset"
          disabled={isSubmitting}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
        >
          <WizardForm defaultValues={{ categoryId: '' }} onSubmit={handleSubmit}>
            <WizardForm.Step label="Choose New Category">
              <ReferenceInput source="categoryId" reference="categories">
                <AutocompleteInput
                  optionText="attributes.name"
                  label="Category"
                  filterToQuery={createFilterQuery()}
                  validate={required()}
                />
              </ReferenceInput>
            </WizardForm.Step>
            <WizardForm.Step label="Update Related Offers">
              <Box sx={{ mb: 2 }}>
                Select rules whose existing offers should be remapped to the new category:
              </Box>
              <DataTable
                data={localList.data}
                isPending={mappingRules.isPending || mappingRules.isLoading}
                selectedIds={[...relatedOfferIds]}
                sort={localList.sort}
                setSort={localList.setSort}
                onToggleItem={(mappingRuleId) =>
                  setRelatedOfferIds((prevIds) =>
                    prevIds.has(mappingRuleId)
                      ? new Set([...prevIds].filter((id) => id !== mappingRuleId))
                      : new Set(prevIds).add(mappingRuleId),
                  )
                }
                onSelect={(ids) => {
                  const selectableIds = localList.data?.map((mappingRule) => mappingRule.id) ?? [];
                  setRelatedOfferIds(new Set(ids.filter((id) => selectableIds.includes(id))));
                }}
                isRowSelectable={() => !isSubmitting}
                bulkActionButtons={<></>}
                bulkActionsToolbar={false}
                rowClick={false}
              >
                <DataTable.Col source="id" label="Id" />
                <DataTable.Col label="Merchant">
                  <ReferenceField
                    source="relationships.merchant.data.id"
                    reference="merchants"
                    link={false}
                  >
                    <TextField source="attributes.merchant_name" />
                  </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="External category" source="attributes.external_category" />
                <DataTable.Col label="Category">
                  <ReferenceField
                    source="relationships.category.data.id"
                    reference="categories"
                    link={false}
                  >
                    <TextField source="attributes.full_name" />
                  </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Offer count">
                  <OfferCountField />
                </DataTable.Col>
              </DataTable>
            </WizardForm.Step>
          </WizardForm>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
